import { SprintAPI } from '../../utils/api';
import { filterDependencies } from '../../components/SprintDetail/hooks/taskActions/utils';
import {
  hasConditionForTask,
  removeTaskFromConditions,
} from '../../components/SprintDetail/shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Shared utilities for campaign nesting operations
 */

/**
 * Check if target is a descendant of the dragged campaign.
 * This prevents circular nesting (e.g., dropping "Parent" into "Child" when Child is already inside Parent)
 */
export const isTargetDescendantOfDragged = (target, draggedId) => {
  let current = target;
  while (current?.parentSprintId) {
    if (current.parentSprintId === draggedId) return true;
    // We'd need to fetch the parent sprint to continue - but checking the target's immediate parent is usually enough
    // since the tree is maintained by the sidebar
    break;
  }
  return false;
};

/**
 * Remove the parent mission node that links to a nested campaign (not just unlink).
 * Cleans dependsOn / unlockConditions on remaining tasks. Returns updated parent or null.
 * @param {Object} [preloadedParent] - Fully-loaded parent sprint to use instead of
 *   fetching (e.g. live view state, which is fresher than the server copy mid-session)
 */
export const removeTaskLinkFromParent = async (parentId, nestedSprintId, preloadedParent = null) => {
  try {
    const parentSprint = preloadedParent || await SprintAPI.get(parentId);
    if (!parentSprint || !Array.isArray(parentSprint.tasks)) {
      return null;
    }

    const linkTask = parentSprint.tasks.find((t) => t.nestedSprintId === nestedSprintId);
    if (!linkTask) {
      return null;
    }

    const taskId = linkTask.id;
    const filteredTasks = parentSprint.tasks
      .filter((t) => t.id !== taskId)
      .map((t) => ({
        ...t,
        dependsOn: filterDependencies(t.dependsOn, taskId),
        unlockConditions: t.unlockConditions && hasConditionForTask(t.unlockConditions, taskId)
          ? removeTaskFromConditions(t.unlockConditions, taskId)
          : t.unlockConditions,
      }));

    const filteredPositions = { ...(parentSprint.nodePositions || {}) };
    delete filteredPositions[taskId];

    const filteredChildOrder = (parentSprint.childOrder || []).filter((id) => id !== nestedSprintId);

    return {
      ...parentSprint,
      tasks: filteredTasks,
      nodePositions: filteredPositions,
      childOrder: filteredChildOrder.length > 0 ? filteredChildOrder : null,
    };
  } catch (err) {
    console.warn('[NESTING] Could not fetch parent sprint:', err.message);
    return null;
  }
};

/**
 * Dispatch event to notify SprintDetail of campaign nesting change.
 */
export const dispatchCampaignNestedEvent = (draggedSprintId, targetSprintId, newParentSprintId) => {
  window.dispatchEvent(new CustomEvent('campaignNested', {
    detail: {
      draggedSprintId,
      targetSprintId,
      newParentSprintId,
    }
  }));
};

/**
 * Dispatch event to notify SprintDetail of tasks/positions update.
 */
export const dispatchTasksUpdatedEvent = (sprintId, tasks, nodePositions) => {
  window.dispatchEvent(new CustomEvent('sprintTasksUpdated', {
    detail: {
      sprintId,
      tasks,
      nodePositions,
    }
  }));
};

/** Cancel pending debounced sync for a sprint (prevents stale saves after cross-campaign moves). */
export const dispatchCancelSprintSyncDebounce = (sprintId) => {
  window.dispatchEvent(new CustomEvent('sprintSyncDebounceCancelled', {
    detail: { sprintId },
  }));
};

/**
 * Dispatch focus events to all views for a newly created mission.
 * Skips if position was provided (user dropped at specific location).
 */
export const dispatchFocusMissionEvents = (missionId, skipIfPositionProvided = false) => {
  if (skipIfPositionProvided) return;

  // Small delay to let React update the UI first
  setTimeout(() => {
    const views = ['flow', 'kanban', 'table', 'todolist', 'gantt', 'neural'];
    views.forEach(view => {
      window.dispatchEvent(new CustomEvent('focusMission', {
        detail: { missionId, view }
      }));
    });
  }, 150);
};
